import * as turf from "@turf/turf"

import { getPreviousSegmentsTotalLength } from "@l2r-front/l2r-geodata"

export function getRoadLinearLocation(feature) {
    return feature.linear_location?.[0] || feature.properties?.linearLocation?.[0]
}

export function isLinearLocationInverted(linearLocation) {
    return linearLocation.start > linearLocation.end
}

export function convertParametricsToLinearLocation(roadFeature, mergedStart, mergedEnd) {
    const roadLinearLocation = getRoadLinearLocation(roadFeature)
    if (!roadLinearLocation) {
        return null
    }

    const road = roadLinearLocation.road
    const roadLength = turf.length(roadFeature)
    const inverted = isLinearLocationInverted(roadLinearLocation)

    // Calculate relative position of start on the road (0 to 1)
    const startSegmentOffset = getPreviousSegmentsTotalLength(roadFeature, mergedStart.segmentIndex) / roadLength
    const startSegmentLength = turf.length(turf.lineString([
        roadFeature.geometry.coordinates[mergedStart.segmentIndex],
        roadFeature.geometry.coordinates[mergedStart.segmentIndex + 1],
    ])) / roadLength
    const startRelativePosition = startSegmentOffset + mergedStart.start * startSegmentLength

    // Calculate relative position of end on the road (0 to 1)
    const endSegmentOffset = getPreviousSegmentsTotalLength(roadFeature, mergedEnd.segmentIndex) / roadLength
    const endSegmentLength = turf.length(turf.lineString([
        roadFeature.geometry.coordinates[mergedEnd.segmentIndex],
        roadFeature.geometry.coordinates[mergedEnd.segmentIndex + 1],
    ])) / roadLength
    const endRelativePosition = endSegmentOffset + mergedEnd.end * endSegmentLength

    // Convert relative positions to linear location values
    const linearRange = Math.abs(roadLinearLocation.end - roadLinearLocation.start)
    const linearStart = !inverted
        ? roadLinearLocation.start + startRelativePosition * linearRange
        : roadLinearLocation.start - startRelativePosition * linearRange

    const linearEnd = !inverted
        ? roadLinearLocation.start + endRelativePosition * linearRange
        : roadLinearLocation.start - endRelativePosition * linearRange

    return { road, start: linearStart, end: linearEnd }
}

export function convertLinearLocationToParametrics(roadFeature, linearLocation) {
    const roadLinearLocation = getRoadLinearLocation(roadFeature)
    if (!roadLinearLocation || !linearLocation) {
        return null
    }

    const roadLength = turf.length(roadFeature)
    const inverted = isLinearLocationInverted(roadLinearLocation)
    const linearRange = Math.abs(roadLinearLocation.end - roadLinearLocation.start)

    const clamp = (val, min, max) => Math.min(Math.max(val, min), max)

    // Convert linear location to relative positions (0 to 1) on the road
    const startRelativePosition = clamp(!inverted
        ? (linearLocation.start - roadLinearLocation.start) / linearRange
        : (roadLinearLocation.start - linearLocation.start) / linearRange, 0, 1)

    const endRelativePosition = clamp(!inverted
        ? (linearLocation.end - roadLinearLocation.start) / linearRange
        : (roadLinearLocation.start - linearLocation.end) / linearRange, 0, 1)

    // Convert relative positions to distances along the road
    const startDistance = startRelativePosition * roadLength
    const endDistance = endRelativePosition * roadLength

    // Find which segment contains start and end points
    let cumulativeLength = 0
    let startSegmentIndex = 0
    let startParametric = 0
    let endSegmentIndex = 0
    let endParametric = 0

    for (let i = 0; i < roadFeature.geometry.coordinates.length - 1; i++) {
        const segmentLine = turf.lineString([
            roadFeature.geometry.coordinates[i],
            roadFeature.geometry.coordinates[i + 1],
        ])
        const segmentLength = turf.length(segmentLine)
        const nextCumulativeLength = cumulativeLength + segmentLength

        if (startDistance >= cumulativeLength && startDistance <= nextCumulativeLength) {
            startSegmentIndex = i
            startParametric = segmentLength > 0 ? clamp((startDistance - cumulativeLength) / segmentLength, 0, 1) : 0
        }

        if (endDistance >= cumulativeLength && endDistance <= nextCumulativeLength) {
            endSegmentIndex = i
            endParametric = segmentLength > 0 ? clamp((endDistance - cumulativeLength) / segmentLength, 0, 1) : 0
        }

        cumulativeLength = nextCumulativeLength
    }

    return {
        mergedStart: {
            segmentIndex: startSegmentIndex,
            start: startParametric,
        },
        mergedEnd: {
            segmentIndex: endSegmentIndex,
            end: endParametric,
        },
    }
}

export function convertLinearLocationToRelativePositions(roadFeature, linearLocation) {
    const roadLinearLocation = getRoadLinearLocation(roadFeature)
    if (!roadLinearLocation) {
        return null
    }

    const roadLength = turf.length(roadFeature)
    const inverted = isLinearLocationInverted(roadLinearLocation)
    const linearRange = Math.abs(roadLinearLocation.end - roadLinearLocation.start)

    const startRelativePosition = !inverted
        ? (linearLocation.start - roadLinearLocation.start) / linearRange
        : (roadLinearLocation.start - linearLocation.start) / linearRange

    const endRelativePosition = !inverted
        ? (linearLocation.end - roadLinearLocation.start) / linearRange
        : (roadLinearLocation.start - linearLocation.end) / linearRange

    return {
        startDistance: startRelativePosition * roadLength,
        endDistance: endRelativePosition * roadLength,
        roadLength,
        inverted,
    }
}
